import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Outcome, Product, ResultEvent, ResultMarket, Selection, View } from '@/results/results.api';
import { clockText, fancyName, fancyView, outcomesText, outcomeWords, whenText } from '@/results/format';
import ActionLog from '@/components/results/actionLog/actionLog.vue';

const PRODUCT_NAMES: Record<number, string> = { 1: 'Match Odds', 2: 'Bookmaker', 3: 'Fancy', 4: 'Premium' };
const RUN_WORDS: Record<string, string> = {
  SETTLE: 'Settling', RESETTLE: 'Correcting', VOID: 'Voiding', UNSETTLE: 'Undoing the result', VOID_BETS: 'Voiding bets',
};

/**
 * GS-894 — what a premium selection can end as (SPORTSBOOK-ENGINE.md §3.6, SB-D3), in the statement's words; the halves
 * only on a quarter line, a dead heat's size set once for the market.
 */
interface OutcomeOption {
  key: string;
  label: string;
  tone: string;
  halves?: boolean;
}
const OUTCOME_OPTIONS: OutcomeOption[] = [
  { key: 'WIN', label: 'Won', tone: 'win' },
  { key: 'LOSE', label: 'Lost', tone: 'lose' },
  { key: 'HALF_WIN', label: 'Half won', tone: 'halfwin', halves: true },
  { key: 'HALF_LOSE', label: 'Half lost', tone: 'halflose', halves: true },
  { key: 'VOID', label: 'Void', tone: 'void' },
  { key: 'DEAD_HEAT', label: 'Dead heat', tone: 'heat' },
];
const DEAD_HEAT = 'DEAD_HEAT_';

/**
 * GS-901 — one match's markets (Match Odds, bookmaker) or lines (fancy) with their result controls: tap the winner (or
 * type the runs), then confirm; an answered one shows its result with Change / Undo / Void bets. The component only
 * says WHAT the operator chose (`declare`, `void-bets`); the console confirms it, sends it and refreshes.
 */
@Component({ components: { ActionLog } })
export default class EventDetail extends Vue {
  @Prop({ required: true }) readonly event!: ResultEvent;
  @Prop({ required: true }) readonly product!: Product;
  @Prop({ required: true }) readonly view!: View;
  @Prop({ default: (): ResultMarket[] => [] }) readonly markets!: ResultMarket[];
  @Prop({ default: false }) readonly loading!: boolean;
  @Prop({ default: '' }) readonly error!: string;

  private picks: Record<string, number> = {};
  private values: Record<string, string> = {};
  private noResult: Record<string, boolean> = {};
  private editing: Record<string, boolean> = {};
  /** GS-894: a premium market's outcome per selection (an option's key) and the size of its dead heat. */
  private outcomes: Record<string, Record<number, string>> = {};
  private heats: Record<string, number> = {};
  /** GS-925: the markets whose history is open. */
  private history: Record<string, boolean> = {};

  toggleHistory(m: ResultMarket): void {
    this.$set(this.history, m.marketId, !this.history[m.marketId]);
  }

  get isFancy(): boolean {
    return this.product === 3;
  }

  get isPremium(): boolean {
    return this.product === 4;
  }

  get productName(): string {
    return PRODUCT_NAMES[this.product] || '';
  }

  get when(): string {
    return whenText(this.event.startTime);
  }

  /**
   * Another match: nothing chosen carries over to it. The same match re-read (the 30 s refresh, another market's run
   * finishing) arrives as a new object with the same key — what the operator is entering on its other markets stays.
   */
  @Watch('event.key')
  onEvent(): void {
    this.picks = {};
    this.values = {};
    this.noResult = {};
    this.editing = {};
    this.outcomes = {};
    this.heats = {};
  }

  @Watch('markets')
  onMarkets(markets: ResultMarket[]): void {
    // a market that came back answered (or with a run) leaves edit mode; its entry is cleared
    const editing = Object.assign({}, this.editing);
    markets.forEach((m) => {
      if (m.activeRun || (m.answered && !this.editing[m.marketId])) {
        delete editing[m.marketId];
      }
    });
    this.editing = editing;
  }

  isEditing(m: ResultMarket): boolean {
    return !!this.editing[m.marketId];
  }

  canChoose(m: ResultMarket): boolean {
    return !m.activeRun && (!m.answered || this.isEditing(m));
  }

  pick(m: ResultMarket, id: number): void {
    this.picks = Object.assign({}, this.picks, { [m.marketId]: id });
  }

  setValue(m: ResultMarket, value: string): void {
    this.values = Object.assign({}, this.values, { [m.marketId]: String(value || '').replace(/[^0-9]/g, '') });
  }

  setNoResult(m: ResultMarket, on: boolean): void {
    this.noResult = Object.assign({}, this.noResult, { [m.marketId]: !!on });
  }

  startEdit(m: ResultMarket): void {
    this.editing = Object.assign({}, this.editing, { [m.marketId]: true });
    if (m.result && m.result.winnerId) {
      this.pick(m, m.result.winnerId);
    }
    if (m.result && m.result.value !== undefined && m.result.value !== null) {
      this.setValue(m, String(m.result.value));
    }
    if (m.result && m.result.outcomes) {
      // GS-894: a correction starts from the outcomes declared now
      const set: Record<number, string> = {};
      m.result.outcomes.forEach((o) => {
        if (o.outcome.indexOf(DEAD_HEAT) === 0) {
          set[o.selectionId] = 'DEAD_HEAT';
          this.heats = Object.assign({}, this.heats, { [m.marketId]: Number(o.outcome.substring(DEAD_HEAT.length)) || 2 });
        } else {
          set[o.selectionId] = o.outcome;
        }
      });
      this.outcomes = Object.assign({}, this.outcomes, { [m.marketId]: set });
    }
  }

  stopEdit(m: ResultMarket): void {
    const editing = Object.assign({}, this.editing);
    delete editing[m.marketId];
    this.editing = editing;
  }

  ready(m: ResultMarket): boolean {
    if (this.isFancy) {
      return !!this.noResult[m.marketId] || (this.values[m.marketId] || '').length > 0;
    }
    if (this.isPremium) {
      return !this.premiumProblem(m);
    }
    const chosen = this.picks[m.marketId];
    if (chosen === undefined) {
      return false;
    }
    // a change must change something
    return !(this.isEditing(m) && m.result && m.result.winnerId === chosen);
  }

  submitLabel(m: ResultMarket): string {
    if (this.isFancy) {
      return this.noResult[m.marketId] ? 'Save: no result' : this.isEditing(m) ? 'Save the new number' : 'Save result';
    }
    if (this.isPremium) {
      return this.unset(m) ? 'Set every outcome' : this.isEditing(m) ? 'Save the change' : 'Save result';
    }
    const chosen = m.selections.find((s) => s.id === this.picks[m.marketId]);
    if (!chosen) {
      return this.isEditing(m) ? 'Pick the new winner' : 'Pick the winner';
    }
    return (this.isEditing(m) ? 'Change to ' : 'Declare ') + chosen.name;
  }

  submit(m: ResultMarket): void {
    if (!this.ready(m)) {
      return;
    }
    // GS-926: a market with NO result is given one by a SETTLE (the engine's table: RESETTLE only from SETTLED)
    const correction = m.answered && this.isEditing(m) && !(m.engine === 'v2' && m.status === 'VOIDED');
    if (this.isFancy) {
      if (this.noResult[m.marketId]) {
        this.$emit('declare', { market: m, action: 'void' });
      } else {
        this.$emit('declare', { market: m, action: correction ? 'resettle' : 'settle', value: Number(this.values[m.marketId]) });
      }
      return;
    }
    if (this.isPremium) {
      const results = this.premiumResults(m);
      this.$emit('declare', { market: m, action: correction ? 'resettle' : 'settle', results, resultText: outcomesText(results) });
      return;
    }
    const chosen = m.selections.find((s) => s.id === this.picks[m.marketId]);
    this.$emit('declare', { market: m, action: correction ? 'resettle' : 'settle', winnerId: this.picks[m.marketId],
      winnerName: chosen ? chosen.name : '' });
  }

  noResultMarket(m: ResultMarket): void {
    this.$emit('declare', { market: m, action: 'void' });
  }

  /** GS-926: stop the bets on an OPEN market before its result (engine v2's CLOSE). */
  closeMarket(m: ResultMarket): void {
    this.$emit('declare', { market: m, action: 'close' });
  }

  status(m: ResultMarket): { text: string; tone: string } {
    if (m.activeRun) {
      return m.activeRun.status === 'AWAITING_APPROVAL' ? { text: 'Needs approval', tone: 'warning' }
        : m.activeRun.status === 'BLOCKED' ? { text: 'Blocked', tone: 'danger' } : { text: 'In progress', tone: 'info' };
    }
    if (m.answered) {
      return m.status === 'VOIDED' ? { text: 'No result', tone: 'muted' } : { text: 'Settled', tone: 'success' };
    }
    return { text: m.status === 'CLOSED' ? 'Closed · to result' : 'To result', tone: 'gold' };
  }

  runText(m: ResultMarket): string {
    const run = m.activeRun;
    if (!run) {
      return '';
    }
    const what = RUN_WORDS[run.action] || run.action;
    if (run.status === 'AWAITING_APPROVAL') {
      return `${what} — waiting for a second supervisor (asked by ${run.requestedBy || 'someone'})`;
    }
    if (run.status === 'BLOCKED') {
      return `${what} is blocked — open the runs queue`;
    }
    return `${what}…`;
  }

  resultText(m: ResultMarket): string {
    const r = m.result;
    if (!r) {
      return '';
    }
    if (r.kind === 'VOID') {
      return r.voidReason ? r.voidReason.replace(/_/g, ' ').toLowerCase() : 'void';
    }
    if (r.kind === 'VALUE') {
      // GS-902: as the statement words it — "87 · digit 7" on a lottery, "87 · odd" on odd/even
      const view = fancyView(r.value, m.subtype);
      return String(r.value) + (view ? ' · ' + view : '');
    }
    if (r.kind === 'OUTCOMES') {
      return outcomesText(r.outcomes || [], this.names(m));
    }
    return r.winnerName || String(r.winnerId || '');
  }

  // ------------------------------------------------------------------------------------------------ GS-894 premium

  /** The outcomes this market may end in: the halves only on a quarter line (x.25 / x.75, SB-D3). */
  options(m: ResultMarket): OutcomeOption[] {
    return OUTCOME_OPTIONS.filter((o) => !o.halves || m.halvesAllowed);
  }

  outcomeOf(m: ResultMarket, s: Selection): string {
    const set = this.outcomes[m.marketId];
    return (set && set[s.id]) || '';
  }

  setOutcome(m: ResultMarket, s: Selection, key: string): void {
    const set = Object.assign({}, this.outcomes[m.marketId] || {}, { [s.id]: key });
    this.outcomes = Object.assign({}, this.outcomes, { [m.marketId]: set });
  }

  /** Every selection still without an outcome becomes Lost — most of a market's selections simply lose. */
  restLost(m: ResultMarket): void {
    const set = Object.assign({}, this.outcomes[m.marketId] || {});
    m.selections.forEach((s) => {
      if (!set[s.id]) {
        set[s.id] = 'LOSE';
      }
    });
    this.outcomes = Object.assign({}, this.outcomes, { [m.marketId]: set });
  }

  unset(m: ResultMarket): number {
    return m.selections.filter((s) => !this.outcomeOf(m, s)).length;
  }

  heatCount(m: ResultMarket): number {
    return m.selections.filter((s) => this.outcomeOf(m, s) === 'DEAD_HEAT').length;
  }

  /** The dead heat's size: how many tied for the place (at least the selections in it, at least 2). */
  heatK(m: ResultMarket): number {
    return Math.max(this.heats[m.marketId] || 0, this.heatCount(m), 2);
  }

  bumpHeat(m: ResultMarket, by: number): void {
    const k = Math.min(Math.max(this.heatK(m) + by, this.heatCount(m), 2), 20);
    this.heats = Object.assign({}, this.heats, { [m.marketId]: k });
  }

  /** What the declaration sends: every selection's outcome, a dead heat with its size (DEAD_HEAT_k). */
  premiumResults(m: ResultMarket): Outcome[] {
    return m.selections.map((s) => {
      const key = this.outcomeOf(m, s);
      return { selectionId: s.id, outcome: key === 'DEAD_HEAT' ? DEAD_HEAT + this.heatK(m) : key, name: s.name };
    });
  }

  /** How many may win, in words, and whether halves are possible — the rules the server checks (§3.6). */
  rulesText(m: ResultMarket): string {
    const k = m.winners || 1;
    return (k === 1 ? 'One winner' : 'Up to ' + k + ' winners') + (m.halvesAllowed ? ' · quarter line: half results possible' : '');
  }

  /**
   * Why the result cannot be saved yet, or '' — the server's own checks, said before it refuses: every selection named,
   * the places (full and half wins, a dead heat as one) no more than the market's k, a correction that changes something.
   */
  premiumProblem(m: ResultMarket): string {
    const missing = this.unset(m);
    if (missing) {
      return missing === m.selections.length ? 'Set an outcome for each selection'
        : `${missing} selection${missing === 1 ? ' still needs' : 's still need'} an outcome`;
    }
    const k = m.winners || 1;
    const results = this.premiumResults(m);
    const wins = results.filter((r) => r.outcome === 'WIN' || r.outcome === 'HALF_WIN').length;
    const places = wins + (this.heatCount(m) ? 1 : 0);
    if (places > k) {
      return `This market has ${k === 1 ? 'one winner' : k + ' winners'}: the result names ${places}`
        + (this.heatCount(m) ? ' (a dead heat counts as one)' : '');
    }
    if (this.isEditing(m) && m.result && m.result.outcomes && this.sameOutcomes(results, m.result.outcomes)) {
      return 'Change at least one outcome';
    }
    return '';
  }

  /** The tone of a problem: a missing outcome is still being entered; the others need a change. */
  problemTone(m: ResultMarket): string {
    return this.unset(m) ? 'muted' : 'warning';
  }

  private sameOutcomes(a: Outcome[], b: Outcome[]): boolean {
    return a.length === b.length && a.every((x) => b.some((y) => y.selectionId === x.selectionId && y.outcome === x.outcome));
  }

  private names(m: ResultMarket): Record<number, string> {
    const names: Record<number, string> = {};
    m.selections.forEach((s) => {
      names[s.id] = s.name;
    });
    return names;
  }

  /** An answered premium market: each selection's outcome, in words, and its tone. */
  declared(m: ResultMarket, s: Selection): { text: string; tone: string } {
    const o = m.result && m.result.outcomes ? m.result.outcomes.find((x) => x.selectionId === s.id) : undefined;
    if (!o) {
      return { text: '—', tone: 'lose' };
    }
    const option = OUTCOME_OPTIONS.find((x) => x.key === (o.outcome.indexOf(DEAD_HEAT) === 0 ? 'DEAD_HEAT' : o.outcome));
    return { text: outcomeWords(o.outcome), tone: option ? option.tone : 'lose' };
  }

  /** A premium market's line and k, beside its name. */
  lineText(m: ResultMarket): string {
    return m.lineSpec ? 'Line ' + m.lineSpec : '';
  }

  /** GS-902: the line's subtype by name (Lottery, Odd / even, Khadda…). */
  kindName(m: ResultMarket): string {
    return fancyName(m.subtype);
  }

  /** GS-902: what the typed number declares on a lottery or odd/even line ("digit 7", "odd"); nothing elsewhere. */
  preview(m: ResultMarket): string {
    const typed = this.values[m.marketId];
    return typed && !this.noResult[m.marketId] ? fancyView(Number(typed), m.subtype) : '';
  }

  placeholder(m: ResultMarket): string {
    return m.subtype === 5 ? 'Score or digit' : m.subtype === 6 ? 'Total runs' : 'Runs';
  }

  clock(iso: string): string {
    return clockText(iso);
  }

  money(amount?: string): string {
    const n = Number(amount || 0);
    return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }
}
